/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { Link } from "@packages/framework";
import type { Menu } from "@packages/sdk";
import {
  URL_COPYRIGHT,
  URL_PRIVACY_POLICY,
  URL_TERMS_OF_SERVICE,
  usePathMatch,
} from "@packages/sdk";
import { Suspense } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  colors,
  numericPercentages,
  numericPixels,
  spacing,
} from "../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import { Container } from "../../../blocks/layout/Container/Container.emotion";
import { HallowLogoText } from "../../icons/HallowLogoText/HallowLogoText.emotion";
import {
  FacebookMinimalIcon,
  InstagramMinimalIcon,
  XMinimalIcon,
} from "../../icons/Icon/ExternalIcons";
import { Text } from "../Text/Text.emotion";
import { AppStoreIcon, PlayStoreIcon } from "./AppStoreIcons";
import { FooterCookies } from "./FooterCookies.emotion";

const links = [
  {
    href: "/settings",
    message: "settings_title",
  },
  { href: URL_PRIVACY_POLICY, message: "web_privacy_policy" },
  {
    href: URL_TERMS_OF_SERVICE,
    message: "web_terms_of_service",
  },
  {
    href: URL_COPYRIGHT,
    message: "web_copyright",
  },
];

const baseStyle: Interpolation = {
  backgroundColor: colors.black,
  color: colors.white,
};

const mainStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.l,
  paddingBottom: spacing.xxl,
  paddingTop: spacing.xxxl,
  [cssMediaQueryMinWidthTablet]: { flexDirection: "row" },
};

const brandStyle: Interpolation = {
  padding: `${spacing.none} ${spacing.l}`,
  [cssMediaQueryMinWidthTablet]: {
    padding: spacing.none,
  },
};

const navStyle: Interpolation = { flexGrow: 1 };

const navListStyle: Interpolation = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
};
const navLinkStyle: Interpolation = {
  display: "block",
  padding: `${spacing.s} ${spacing.l}`,
  textDecoration: "none",
  ":hover": {
    textDecoration: "underline",
  },
};
const linksStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  flexGrow: 0,
  flexShrink: 0,
  gap: spacing.s,
  padding: `${spacing.s} ${spacing.l}`,
  [cssMediaQueryMinWidthTablet]: { padding: spacing.none },
};
const socialStyle: Interpolation = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  paddingBottom: spacing.s,
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
  paddingTop: spacing.none,
  width: numericPixels[200],
  [cssMediaQueryMinWidthTablet]: { width: numericPercentages[100] },
};
const trailerStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  color: theme.semanticColors.neutralsMedHigh,
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  paddingBottom: spacing.xl,
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
  paddingTop: spacing.m,
  [cssMediaQueryMinWidthTablet]: {
    flexDirection: "row",
    gap: spacing.none,
    justifyContent: "space-between",
    paddingTop: spacing.xl,
  },
});
const ulStyle: Interpolation = {
  alignItems: "flex-start",
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.m,
  justifyContent: "center",
  padding: `${spacing.none} ${spacing.l}`,
  [cssMediaQueryMinWidthTablet]: {
    alignItems: "center",
    flexWrap: "nowrap",
    gap: spacing.l,
    justifyContent: "space-between",
    padding: spacing.none,
  },
};
const legalLinkStyle: Interpolation = {
  flexShrink: 0,
};

type FooterProps = {
  menu: Menu;
};

export const Footer = ({ menu }: FooterProps) => {
  const matcher = usePathMatch();
  const isWebCheckout = matcher("/web/subscribe");

  const t = useTranslations();
  const analytics = useAnalytics();

  return (
    <footer css={baseStyle}>
      <Container maxWidth={true}>
        {!isWebCheckout && (
          <div css={mainStyle}>
            <div css={brandStyle}>
              <HallowLogoText />
            </div>
            <nav css={navStyle}>
              <ul css={navListStyle}>
                {menu.map((menu) => (
                  <li key={menu.id}>
                    <a href={menu.url} target={menu.target} css={navLinkStyle}>
                      <Text size="l" type="body">
                        {menu.title}
                      </Text>
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
            <div css={linksStyle}>
              <div css={socialStyle}>
                <Link href="https://facebook.com/hallowapp" target="_blank">
                  <FacebookMinimalIcon />
                </Link>
                <Link href="https://x.com/HallowApp" target="_blank">
                  <XMinimalIcon />
                </Link>
                <Link href="https://instagram.com/hallowapp" target="_blank">
                  <InstagramMinimalIcon />
                </Link>
              </div>
              <Link
                href="https://itunes.apple.com/app/id1405323394"
                target="_blank"
                onClickCapture={() => {
                  analytics.track({
                    event: HallowAnalyticsEvent.TappedDownloadOnTheAppStore,
                    properties: {
                      location: "footer",
                    },
                  });
                }}
              >
                <AppStoreIcon />
              </Link>
              <Link
                href="https://play.google.com/store/apps/details?id=app.hallow.android"
                target="_blank"
                onClickCapture={() => {
                  analytics.track({
                    event: HallowAnalyticsEvent.TappedGetItOnGooglePlay,
                    properties: {
                      location: "footer",
                    },
                  });
                }}
              >
                <PlayStoreIcon />
              </Link>
            </div>
          </div>
        )}
        <div css={trailerStyle}>
          <Text size="xl" type="detail">
            © {new Date().getFullYear()} Hallow, Inc.
          </Text>
          <ul css={ulStyle}>
            <li key={"cookie-manager"}>
              <Suspense>
                <FooterCookies />
              </Suspense>
            </li>
            {links.map((link) => (
              <li key={link.message} css={legalLinkStyle}>
                <Link href={link.href} target="_blank">
                  <Text size="xl" type="detail">
                    {t(link.message)}
                  </Text>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </footer>
  );
};

Footer.displayName = "Footer";
